import { Activity, ActivityProgress, UserStats, Streak } from '@/types';

const DB_NAME = 'streaks-app';
const DB_VERSION = 1;

export class Database {
  private db: IDBDatabase | null = null;

  async init(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        if (!db.objectStoreNames.contains('activities')) {
          const activitiesStore = db.createObjectStore('activities', { keyPath: 'id' });
          activitiesStore.createIndex('category', 'category', { unique: false });
          activitiesStore.createIndex('isActive', 'isActive', { unique: false });
        }

        if (!db.objectStoreNames.contains('progress')) {
          const progressStore = db.createObjectStore('progress', { keyPath: 'id' });
          progressStore.createIndex('activityId', 'activityId', { unique: false });
          progressStore.createIndex('completedAt', 'completedAt', { unique: false });
        }

        if (!db.objectStoreNames.contains('streaks')) {
          db.createObjectStore('streaks', { keyPath: 'activityId' });
        }

        if (!db.objectStoreNames.contains('stats')) {
          db.createObjectStore('stats', { keyPath: 'id' });
        }
      };
    });
  }

  async getActivities(): Promise<Activity[]> {
    return this.getAll('activities');
  }

  async addActivity(activity: Activity): Promise<void> {
    await this.add('activities', activity);
  }

  async updateActivity(activity: Activity): Promise<void> {
    await this.update('activities', activity);
  }

  async deleteActivity(id: string): Promise<void> {
    await this.delete('activities', id);
  }

  async getProgress(activityId?: string): Promise<ActivityProgress[]> {
    if (activityId) {
      return this.getByIndex('progress', 'activityId', activityId);
    }
    return this.getAll('progress');
  }

  async addProgress(progress: ActivityProgress): Promise<void> {
    await this.add('progress', progress);
  }

  async getStreaks(): Promise<Streak[]> {
    return this.getAll('streaks');
  }

  async updateStreak(streak: Streak): Promise<void> {
    await this.update('streaks', streak);
  }

  async getUserStats(): Promise<UserStats> {
    const stats = await this.get<UserStats>('stats', 'user');
    return stats || {
      totalPoints: 0,
      totalActivities: 0,
      longestStreak: 0,
      currentStreak: 0,
      activitiesCompleted: 0,
      level: 1
    };
  }

  async updateUserStats(stats: UserStats): Promise<void> {
    await this.update('stats', { ...stats, id: 'user' });
  }

  private async getAll<T>(storeName: string): Promise<T[]> {
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([storeName], 'readonly');
      const store = transaction.objectStore(storeName);
      const request = store.getAll();

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  private async get<T>(storeName: string, key: string): Promise<T | undefined> {
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([storeName], 'readonly');
      const store = transaction.objectStore(storeName);
      const request = store.get(key);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  private async getByIndex<T>(storeName: string, indexName: string, key: string): Promise<T[]> {
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([storeName], 'readonly');
      const store = transaction.objectStore(storeName);
      const index = store.index(indexName);
      const request = index.getAll(key);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  private async add<T>(storeName: string, data: T): Promise<void> {
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.add(data);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  private async update<T>(storeName: string, data: T): Promise<void> {
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.put(data);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  private async delete(storeName: string, key: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.delete(key);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }
}

export const db = new Database();